<template>
  <ResponsiveDialog
    v-model="visible"
    :title="isEdit ? '업무 수정' : '업무 추가'"
    width="560px"
    :footer-spread="false"
    @closed="onClosed"
  >
    <!--
      예전에는 라벨 12줄짜리 el-form 이었다. 390px 화면에서 '제목' 을 치고 나면
      '추가' 버튼까지 두 번 스크롤해야 했고, 실제로 매번 채우는 값은
      제목 · 마감 · 우선순위 · 담당자 네 개뿐인데 나머지 여덟 줄이 그 사이를 갈라놓았다.
      자주 쓰는 것만 위에 두고 나머지는 '자세히' 뒤로 넣는다.
    -->
    <el-form ref="formRef" :model="form" :rules="rules" label-position="top" class="td">
      <!-- 제목은 라벨을 지웠다. placeholder 가 곧 라벨이고, 시트를 열었을 때
           가장 먼저 눈에 들어와야 하는 것이 입력칸 자체다. -->
      <el-form-item prop="title" class="td-title">
        <el-input v-model="form.title" maxlength="255" placeholder="무엇을 해야 하나요?" />
      </el-form-item>

      <div class="td-desc">
        <el-input
          v-model="form.description"
          type="textarea"
          :rows="3"
          maxlength="1000"
          resize="none"
          placeholder="설명을 덧붙이면 담당자가 다시 묻지 않습니다. (선택)"
        />
      </div>

      <!-- 마감.
           el-date-picker 를 기본으로 두면 오늘/내일 하나 고르는 데도 팝오버를 열고
           달을 확인하고 날짜를 찍는 세 동작이 든다. 실제 입력의 대부분은 오늘·내일·이번 주라
           그 셋을 칩으로 꺼내 놓고, 그 밖의 날짜만 달력으로 보낸다.
           시작일은 '자세히' 안으로 내렸다 — 대부분의 업무는 마감만 정한다. -->
      <div class="td-field">
        <span class="field-label">마감</span>
        <div class="td-chips">
          <button
            v-for="q in dueQuick"
            :key="q.key"
            type="button"
            class="td-chip"
            :class="{ 'is-on': form.dueDate === q.value }"
            @click="setDue(q.value)"
          >{{ q.label }}</button>

          <button
            type="button"
            class="td-chip td-chip--cal"
            :class="{ 'is-on': isCustomDue }"
            aria-label="달력에서 마감일 고르기"
            @click="openDuePicker"
          >
            <el-icon><Calendar /></el-icon>
            <span v-if="isCustomDue" class="u-num">{{ form.dueDate }}</span>
          </button>

          <button
            v-if="form.dueDate"
            type="button"
            class="td-chip td-chip--clear"
            aria-label="마감일 지우기"
            @click="setDue(null)"
          >
            <el-icon><Close /></el-icon>
          </button>
        </div>

        <el-date-picker
          v-show="duePickerOpen"
          ref="duePickerRef"
          v-model="form.dueDate"
          type="date"
          value-format="YYYY-MM-DD"
          format="YYYY-MM-DD"
          placeholder="마감일"
          class="td-picker"
          clearable
        />
      </div>

      <!-- 우선순위. 세 개뿐인 선택지를 드롭다운에 넣어 두면 탭이 두 번(열기+고르기) 든다. -->
      <div class="td-field">
        <span class="field-label">우선순위</span>
        <div class="td-chips">
          <button
            v-for="p in PRIORITY_LIST"
            :key="p.value"
            type="button"
            class="td-chip"
            :class="[`td-chip--pri-${p.value}`, { 'is-on': form.priority === p.value }]"
            @click="form.priority = p.value"
          >
            <span class="td-dot" :style="{ background: p.color }" />{{ p.label }}
          </button>
        </div>
      </div>

      <!-- 담당자.
           실제로는 대부분 자기 업무를 만드는데, 예전 폼에서는 el-select 를 열고
           목록에서 자기 이름을 찾아야 했다('나에게' 버튼은 셀렉트 오른쪽에 있어
           셀렉트가 먼저 눈에 들어왔다). 가장 잦은 선택을 가장 큰 칩으로 앞에 둔다. -->
      <div v-if="!isEdit" class="td-field">
        <span class="field-label">담당자</span>
        <div class="td-chips">
          <button
            type="button"
            class="td-chip td-chip--me"
            :class="{ 'is-on': assignMode === 'me' }"
            @click="setAssign('me')"
          >
            <el-avatar :size="26" :src="myAvatar">{{ myInitial }}</el-avatar>
            나에게
          </button>
          <button
            type="button"
            class="td-chip"
            :class="{ 'is-on': assignMode === 'other' }"
            @click="setAssign('other')"
          >다른 사람</button>
          <button
            type="button"
            class="td-chip"
            :class="{ 'is-on': assignMode === 'none' }"
            @click="setAssign('none')"
          >미배정</button>
        </div>

        <el-select
          v-if="assignMode === 'other'"
          v-model="form.assigneeId"
          class="td-select"
          clearable
          filterable
          placeholder="담당자 선택"
        >
          <el-option v-for="u in otherUsers" :key="u.id" :label="u.nickname" :value="u.id" />
        </el-select>
        <p v-if="assignMode === 'none'" class="td-hint">
          미배정 업무는 다른 사람이 '가져가기' 로 담당자가 됩니다.
        </p>
      </div>

      <!--
        상태는 수정할 때만 나온다.
        '승인대기' 는 **고를 수 없다.** 완료 요청 API 로만 진입하는 상태이고
        (taskStatus.js 의 selectable:false, 서버 SETTABLE_STATUSES), 여기서 고르면
        updateTask 는 성공한 채 상태 변경만 400 으로 떨어져서 — 저장은 됐는데
        에러 토스트가 뜨고 시트도 안 닫히고 목록도 갱신되지 않는다.
        현재 상태가 승인대기일 때만 '지금 이 상태' 를 보여주려고 목록에 남긴다.
      -->
      <div v-if="isEdit || presetStatus" class="td-field">
        <span class="field-label">상태</span>
        <div class="td-chips">
          <button
            v-for="s in STATUS_LIST"
            :key="s.value"
            type="button"
            class="td-chip"
            :disabled="s.selectable === false && form.status !== s.value"
            :class="[`td-chip--st-${s.value}`, { 'is-on': form.status === s.value }]"
            @click="s.selectable === false || (form.status = s.value)"
          >
            <span class="td-dot" :style="{ background: s.color }" />{{ s.label }}
          </button>
        </div>
      </div>

      <button
        type="button"
        class="td-more"
        :aria-expanded="moreOpen ? 'true' : 'false'"
        @click="moreOpen = !moreOpen"
      >
        자세히
        <el-icon class="td-more__caret" :class="{ 'is-open': moreOpen }"><ArrowDown /></el-icon>
      </button>

      <div v-show="moreOpen" class="td-more-body">
        <div class="td-field">
          <span class="field-label">시작일</span>
          <el-date-picker
            v-model="form.startDate"
            type="date"
            value-format="YYYY-MM-DD"
            format="YYYY-MM-DD"
            placeholder="여러 날에 걸치는 업무만"
            class="td-picker"
            clearable
          />
          <p class="td-hint">시작일과 마감일이 다르면 캘린더에 기간으로 표시됩니다. 둘 다 비우면 기한 없는 업무.</p>
        </div>

        <div class="td-field">
          <span class="field-label">카테고리</span>
          <el-input v-model="form.category" maxlength="100" placeholder="선택 입력" />
        </div>

        <div v-if="!isEdit" class="td-field">
          <span class="field-label">서포터</span>
          <el-select
            v-model="form.supporterIds"
            class="td-select"
            multiple
            filterable
            collapse-tags
            collapse-tags-tooltip
            placeholder="협업자 (선택)"
          >
            <el-option v-for="u in supporterCandidates" :key="u.id" :label="u.nickname" :value="u.id" />
          </el-select>
        </div>

        <!--
          결재자(결재선).
          이 필드가 없어서 **완료 승인 기능 자체에 들어갈 입구가 없었다.**
          서버는 처음부터 POST/PUT 에서 approverId 를 받고 있었고, 지정된
          업무만 완료 시 승인을 타는데(taskApprovalService 의 불변식:
          결재를 탄다 ⟺ approverId != null), 정작 지정하는 화면이 없었다.
          상세 화면의 '승인자 변경' 은 이미 승인 대기인 업무에서만 열린다 —
          승인 대기가 되려면 먼저 결재자가 있어야 하니 순환이었다.

          기본은 '없음' 이다. 결재는 명시적으로 거는 것이지 기본값이 아니다.
        -->
        <div class="td-field">
          <span class="field-label">완료 결재자</span>
          <el-select
            v-model="form.approverId"
            class="td-select"
            clearable
            filterable
            placeholder="없음 (승인 없이 바로 완료)"
          >
            <el-option
              v-for="u in approverCandidates"
              :key="u.id"
              :label="u.nickname + (u.position ? ` · ${u.position.name}` : '')"
              :value="u.id"
            />
          </el-select>
          <p class="td-hint">
            <template v-if="form.approverId">
              담당자가 완료를 누르면 바로 완료되지 않고 이 사람의 결재함으로 갑니다.
            </template>
            <template v-else>
              지정하면 담당자가 완료를 누를 때 승인을 거칩니다. 비워 두면 바로 완료됩니다.
            </template>
          </p>
        </div>

        <div v-if="showDeptField" class="td-field">
          <span class="field-label">가져갈 부서</span>
          <el-select
            v-model="form.departmentId"
            class="td-select"
            clearable
            filterable
            placeholder="전체 (누구나 가져갈 수 있음)"
          >
            <el-option v-for="d in departments" :key="d.id" :label="d.name" :value="d.id" />
          </el-select>
          <p class="td-hint">담당자 미배정 시 이 부서 구성원만 '가져가기' 가능 · 담당자를 정하면 그 담당자의 부서로 자동 전환됩니다.</p>
        </div>

        <div class="td-switch">
          <div class="td-switch__text">
            <span class="field-label">비공개</span>
            <p class="td-hint">담당자·서포터만 볼 수 있습니다.</p>
          </div>
          <el-switch v-model="form.isPrivate" />
        </div>
      </div>
    </el-form>

    <template #footer>
      <!-- 모바일 푸터는 엄지 반경이다. '취소' 를 여기 두지 않는 이유: 시트에는 이미
           헤더 X 와 스크림 탭이 있고, 그 자리는 한 번에 여러 건을 넣는 '계속 추가' 가
           쓰는 편이 낫다(회의 끝나고 할 일 대여섯 개를 옮겨 적는 상황). -->
      <template v-if="isMobile">
        <el-button v-if="!isEdit" class="td-fmore" :disabled="saving" @click="submitMore">계속 추가</el-button>
        <el-button class="td-fmain" type="primary" :loading="saving" @click="submit">
          {{ isEdit ? '저장' : '추가' }}
        </el-button>
      </template>
      <template v-else>
        <el-button @click="visible = false">취소</el-button>
        <el-button type="primary" :loading="saving" @click="submit">{{ isEdit ? '저장' : '추가' }}</el-button>
      </template>
    </template>
  </ResponsiveDialog>
</template>

<script>
import { ref, reactive, computed, watch, nextTick } from 'vue'
import { useStore } from 'vuex'
import { ElMessage } from 'element-plus'
import { Calendar, ArrowDown, Close } from '@element-plus/icons-vue'
import ResponsiveDialog from '@/components/dialogs/ResponsiveDialog.vue'
import { useBreakpoint } from '@/composables/useBreakpoint'
import { TASK_PRIORITY_LIST, TASK_STATUS_LIST, SELECTABLE_STATUSES } from '@/utils/taskStatus'
import { ymd, addDaysYmd } from '@/utils/agendaItem'
import { confirmStatusChange } from '@/utils/taskUtils'
import { getProfileImageUrl, getProfileInitial } from '@/utils/profileUtils'

export default {
  name: 'TaskDialog',
  components: { ResponsiveDialog, Calendar, ArrowDown, Close },
  props: {
    modelValue: { type: Boolean, default: false },
    task: { type: Object, default: null },
    presetDate: { type: String, default: null },
    // 보드 컬럼 헤더의 '+' 로 열었을 때 그 컬럼의 상태. 주면 추가 시트에도 상태 칩이 나온다.
    presetStatus: { type: String, default: null }
  },
  emits: ['update:modelValue', 'saved'],
  setup(props, { emit }) {
    const store = useStore()
    const { isMobile } = useBreakpoint()
    const formRef = ref(null)
    const saving = ref(false)
    const duePickerRef = ref(null)
    const duePickerOpen = ref(false)
    const moreOpen = ref(false)
    // '다른 사람' 을 눌렀지만 아직 아무도 고르지 않은 상태. assigneeId 만으로는
    // 이 상태와 '미배정' 을 구별할 수 없다(둘 다 null).
    const pickingOther = ref(false)
    // '계속 추가' 로 저장했는지. submit() 끝에서 시트를 닫을지 폼만 비울지를 가른다.
    const addMore = ref(false)
    // 열 때마다 다시 잡는다. 이 앱은 하루 종일 띄워 두는 화면이라 setup 시점에
    // 고정하면 자정을 넘긴 뒤 '오늘' 칩이 어제를 가리킨다.
    const today = ref(ymd(new Date()))

    const visible = computed({
      get: () => props.modelValue,
      set: (v) => emit('update:modelValue', v)
    })
    const isEdit = computed(() => !!props.task)
    // 가져갈 부서(claim scope)는 담당자가 없을 때만 의미가 있음
    const showDeptField = computed(() => (isEdit.value ? !props.task.assigneeId : !form.assigneeId))

    const users = computed(() => store.state.orgUsers || [])
    const departments = computed(() => store.state.orgDepartments || [])

    const form = reactive({
      title: '', description: '', startDate: null, dueDate: null, priority: 'medium',
      category: '', departmentId: null, assigneeId: null, supporterIds: [],
      approverId: null, isPrivate: false, status: 'pending'
    })

    const supporterCandidates = computed(() =>
      users.value.filter((u) => u.id !== form.assigneeId)
    )

    /*
     * 결재자 후보 — 서버 validateTaskApprover 와 **같은 규칙**이어야 한다.
     * 어긋나면 고를 수는 있는데 저장에서 400 이 나고, 사용자는 왜 안 되는지 모른다.
     *   활성 · grade>=2(외부인 제외) · 담당자 본인 아님
     */
    // 수정 모드에는 담당자 칩이 없어(담당자 변경은 별도 시트다) form.assigneeId 가
    // 비어 있다. 그 값으로 거르면 담당자 본인이 결재자 후보에 남는다.
    const effectiveAssigneeId = computed(() =>
      (isEdit.value ? props.task?.assigneeId : form.assigneeId) ?? null
    )

    const approverCandidates = computed(() =>
      users.value.filter((u) =>
        u.isActive !== false &&
        Number(u.grade) >= 2 &&
        Number(u.id) !== Number(effectiveAssigneeId.value)
      )
    )

    /*
     * 담당자를 결재자로도 골라 둔 채 담당자를 바꾸면 서버가 400 을 낸다
     * ('담당자 본인은 결재자가 될 수 없습니다'). 저장 버튼을 눌러야 알게 되므로
     * 담당자가 바뀌는 순간 결재자 쪽을 비운다.
     */
    watch(() => form.assigneeId, (v) => {
      if (v != null && Number(form.approverId) === Number(v)) form.approverId = null
    })

    const me = computed(() => store.state.user || null)
    const myAvatar = computed(() => getProfileImageUrl(me.value))
    const myInitial = computed(() => getProfileInitial(me.value))
    // '나에게' 칩이 따로 있으므로 '다른 사람' 목록에서 자기 자신은 뺀다.
    const otherUsers = computed(() =>
      users.value.filter((u) => Number(u.id) !== Number(me.value?.id))
    )

    const assignMode = computed(() => {
      if (pickingOther.value) return 'other'
      if (!form.assigneeId) return 'none'
      return Number(form.assigneeId) === Number(me.value?.id) ? 'me' : 'other'
    })

    /*
     * 마감 빠른 선택.
     * '이번 주' 는 업무 목록 필터(Tasks.vue thisWeek)와 같은 월~일 기준의 일요일이다.
     * 목록을 '이번 주' 로 걸러 둔 채 여기서 다른 주말을 잡으면 방금 만든 업무가
     * 그 목록에서 곧바로 사라진다.
     * 토·일에는 '이번 주' 가 '내일'/'오늘' 과 같은 날이 되므로 중복 칩을 지운다 —
     * 같은 날짜를 가리키는 칩 두 개가 동시에 켜지면 무엇이 선택된 건지 읽히지 않는다.
     */
    const dueQuick = computed(() => {
      const t = today.value
      const dow = new Date(`${t}T00:00:00`).getDay()
      const toSun = dow === 0 ? 0 : 7 - dow
      const list = [
        { key: 'today', label: '오늘', value: t },
        { key: 'tomorrow', label: '내일', value: addDaysYmd(t, 1) },
        { key: 'week', label: '이번 주', value: addDaysYmd(t, toSun) }
      ]
      return list.filter((q, i) => list.findIndex((x) => x.value === q.value) === i)
    })

    const isCustomDue = computed(
      () => !!form.dueDate && !dueQuick.value.some((q) => q.value === form.dueDate)
    )

    /*
     * 상태 칩 목록.
     * 평소에는 고를 수 있는 상태만(SELECTABLE_STATUSES — 승인대기 제외).
     * 지금 상태가 승인대기면 그 칩도 보여준다 — 안 그러면 "지금 무슨 상태인지" 가
     * 화면에서 사라진다. 대신 disabled 라 되돌아갈 수는 없다.
     */
    const statusChips = computed(() =>
      form.status === 'pending_approval' ? TASK_STATUS_LIST : SELECTABLE_STATUSES
    )

    const rules = {
      title: [{ required: true, message: '제목을 입력해주세요.', trigger: 'blur' }]
    }

    function resetForm() {
      today.value = ymd(new Date())
      duePickerOpen.value = false
      pickingOther.value = false
      if (props.task) {
        form.title = props.task.title || ''
        form.description = props.task.description || ''
        form.startDate = props.task.startDate || null
        form.dueDate = props.task.dueDate || null
        form.priority = props.task.priority || 'medium'
        form.category = props.task.category || ''
        form.departmentId = props.task.departmentId || null
        form.isPrivate = !!props.task.isPrivate
        form.status = props.task.status || 'pending'
        form.approverId = props.task.approverId || null
      } else {
        form.title = ''
        form.description = ''
        // presetDate 는 '마감' 이다. 캘린더에서 9/10 을 눌러 업무를 만들면
        // 사용자가 뜻한 것은 "9/10 까지" 이지 "9/10 에 시작" 이 아니다.
        // (소비자: views/Calendar.vue 의 :preset-date="composeDate")
        // 이전에는 startDate 로 들어가서 (1) 마감이 빈 채로 저장되고
        // (2) startDate 가 채워진 탓에 아래 moreOpen 이 참이 되어 '자세히' 가
        // 늘 펼쳐진 채로 열렸다. 둘 다 이 한 줄에서 나온 증상이다.
        form.startDate = null
        form.dueDate = props.presetDate || null
        form.priority = 'medium'
        form.category = ''
        form.departmentId = null
        form.assigneeId = null
        form.supporterIds = []
        form.isPrivate = false
        form.status = SELECTABLE_STATUSES.some((x) => x.value === props.presetStatus) ? props.presetStatus : 'pending'
        form.approverId = null
      }
      // 접힘 안에 이미 값이 들어 있는데 접어 두면 "값이 사라졌다" 로 읽힌다.
      // (presetDate 는 이제 dueDate 로 들어가고 dueDate 는 접힘 밖에 있으므로
      //  캘린더 진입만으로 '자세히' 가 펼쳐지지는 않는다.)
      moreOpen.value = !!(form.startDate || form.category || form.departmentId || form.isPrivate)
    }

    watch(() => props.modelValue, (v) => { if (v) resetForm() })

    function assignMe() {
      form.assigneeId = store.state.user?.id || null
    }

    function setAssign(mode) {
      if (mode === 'me') {
        pickingOther.value = false
        assignMe()
      } else if (mode === 'none') {
        pickingOther.value = false
        form.assigneeId = null
      } else {
        pickingOther.value = true
        // 내 id 를 그대로 둔 채 셀렉트를 펴면 '다른 사람' 인데 내 이름이 들어 있다.
        if (Number(form.assigneeId) === Number(me.value?.id)) form.assigneeId = null
      }
    }

    function setDue(v) {
      form.dueDate = v
      duePickerOpen.value = false
    }

    // 달력 칩 → 그 자리에서 el-date-picker 를 펴고 패널까지 연다.
    // 보인 뒤에 열어야 패널 위치가 입력칸 기준으로 잡히므로 nextTick 을 거친다.
    function openDuePicker() {
      duePickerOpen.value = true
      nextTick(() => {
        const p = duePickerRef.value
        if (!p) return
        if (typeof p.handleOpen === 'function') p.handleOpen()
        else if (typeof p.focus === 'function') p.focus()
      })
    }

    async function submit() {
      /* 플래그는 들어오자마자 비운다. 검증 실패로 아래에서 return 하면 플래그가 남고,
         그다음에 누른 '추가' 가 '계속 추가' 처럼 굴어 시트가 안 닫힌다. */
      const keepOpen = addMore.value
      addMore.value = false
      try {
        await formRef.value.validate()
      } catch (e) { return }
      if (form.startDate && form.dueDate && form.startDate > form.dueDate) {
        ElMessage.warning('마감일은 시작일과 같거나 이후여야 합니다.')
        return
      }
      // 승인대기에서 다른 상태로 옮기면 서버가 완료 요청을 철회한다. 저장 전에 묻는다.
      const statusChanged = isEdit.value && form.status !== props.task.status
      if (statusChanged && !(await confirmStatusChange(props.task, form.status, store.getters.isAdmin))) return
      saving.value = true
      try {
        const payload = {
          title: form.title.trim(),
          description: form.description || null,
          startDate: form.startDate || null,
          dueDate: form.dueDate || null,
          priority: form.priority,
          category: form.category || null,
          isPrivate: form.isPrivate,
          /* 항상 보낸다(빈 값이면 null). undefined 로 빼면 서버 PUT 이
             `approverId !== undefined` 로 분기하므로 **해제가 저장되지 않는다** —
             결재자를 지웠는데 그대로 남는 증상이 된다. */
          approverId: form.approverId || null
        }
        // 가져갈 부서는 미배정일 때만 의미 → 그 경우에만 전송(배정 시 백엔드가 담당자 부서로 자동 설정)
        if (showDeptField.value) payload.departmentId = form.departmentId || null
        if (isEdit.value) {
          await store.dispatch('updateTask', { id: props.task.id, data: payload })
          if (statusChanged) {
            try {
              await store.dispatch('setTaskStatus', { id: props.task.id, status: form.status })
            } catch (e) {
              // 승인 필요한 업무는 여기서 완료로 못 바꾼다. 나머지 수정은 이미 저장됐다.
              if (!e.handled) throw e
              ElMessage.warning('완료 처리는 상세 화면에서 완료 요청으로 진행해주세요.')
            }
          }
          ElMessage.success('업무가 수정되었습니다.')
        } else {
          payload.assigneeId = form.assigneeId || null
          payload.supporterIds = form.supporterIds || []
          payload.status = form.status
          const res = await store.dispatch('createTask', payload)
          ElMessage.success(res.count ? `${res.count}개의 업무가 생성되었습니다.` : '업무가 생성되었습니다.')
        }
        emit('saved')
        /* '계속 추가' 는 시트를 닫지 않고 폼만 비운다. 목록 갱신은 saved 로 이미
           나갔으므로 뒤에 남는 화면은 최신이고, 다음 한 건은 FAB → 시트 열기
           두 탭을 건너뛴다. 수정 모드에는 없는 동작이다. */
        if (keepOpen && !isEdit.value) {
          resetForm()
          formRef.value?.clearValidate?.()
        } else {
          visible.value = false
        }
      } catch (e) {
        ElMessage.error(e.response?.data?.message || '저장에 실패했습니다.')
      } finally {
        saving.value = false
      }
    }

    function submitMore() {
      addMore.value = true
      return submit()
    }

    function onClosed() { saving.value = false }

    return {
      visible, isEdit, isMobile, showDeptField, formRef, form, rules, saving,
      departments, supporterCandidates, approverCandidates, otherUsers,
      myAvatar, myInitial, assignMode, setAssign,
      dueQuick, isCustomDue, setDue, openDuePicker, duePickerRef, duePickerOpen, moreOpen,
      PRIORITY_LIST: TASK_PRIORITY_LIST, STATUS_LIST: statusChips,
      submit, submitMore, onClosed
    }
  }
}
</script>

<style scoped>
.td { display: flex; flex-direction: column; gap: var(--sp-4); }
.td :deep(.el-form-item) { margin-bottom: 0; }

/* ---------- 제목 ----------
   테두리 상자 대신 밑줄 하나. 시트를 열었을 때 커서가 갈 곳이 하나뿐이라는 걸
   모양으로 말한다. 19px 은 문서 제목과 같은 크기다(TaskDetailPanel .dp-title). */
.td-title :deep(.el-input__wrapper) {
  padding: 0 2px 8px;
  background: transparent;
  border-radius: 0;
  box-shadow: none;
  border-bottom: 2px solid var(--color-primary);
}
.td-title :deep(.el-input__wrapper:hover),
.td-title :deep(.el-input__wrapper.is-focus) {
  box-shadow: none;
  border-bottom-color: var(--color-primary);
}
.td-title :deep(.el-input__inner) {
  height: 34px;
  font-size: var(--fs-title);
  font-weight: var(--fw-semibold);
  letter-spacing: -0.02em;
  color: var(--color-text);
}
.td-title :deep(.el-input__inner::placeholder) {
  font-weight: var(--fw-medium);
  color: var(--color-text-subtle);
}

/* ---------- 설명 ----------
   카드 안에 넣고 입력 자체의 테두리는 지운다. 제목 밑줄과 두 겹의 상자가
   같이 보이면 어느 쪽이 본문인지 흐려진다. */
.td-desc {
  padding: var(--sp-3);
  background: var(--color-surface-3);
  border: 1px solid var(--color-border);
  border-radius: var(--r-md);
}
.td-desc:focus-within { border-color: var(--color-primary); }
.td-desc :deep(.el-textarea__inner) {
  padding: 0;
  background: transparent;
  box-shadow: none;
  font-family: inherit;
  font-size: var(--fs-md);
  line-height: 1.6;
  color: var(--color-text);
}

/* ---------- 필드 ---------- */
.td-field { display: flex; flex-direction: column; }
.td-hint {
  margin: var(--sp-2) 0 0;
  font-size: var(--fs-xs);
  line-height: 1.5;
  color: var(--color-text-subtle);
}

.td-chips { display: flex; flex-wrap: wrap; gap: var(--sp-2); }

/* 칩 자체는 40px 이지만 히트 영역은 위아래로 2px 씩 넓혀 44px 을 만든다.
   ::after 는 흐름 밖이라 줄 높이는 그대로고, 칩 사이 가로 간격이 8px 이라
   좌우로는 넓히지 않아도 옆 칩을 잘못 누를 일이 없다. */
.td-chip {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  height: 40px;
  padding: 0 var(--sp-3);
  border: 1px solid var(--color-border);
  border-radius: var(--r-full);
  background: var(--color-surface);
  font-family: inherit;
  font-size: var(--fs-md);
  font-weight: var(--fw-medium);
  color: var(--color-text-muted);
  white-space: nowrap;
  cursor: pointer;
}
/* 고를 수 없는 상태(승인대기)는 '지금 이 상태' 를 보여주기 위해서만 남아 있다.
   눌러도 아무 일이 없는 칩이라는 걸 눈으로도 알려 준다. */
.td-chip:disabled {
  opacity: 0.55;
  cursor: default;
}

.td-chip::after { content: ''; position: absolute; left: 0; right: 0; top: -2px; bottom: -2px; }
.td-chip:active { background: var(--color-surface-2); }
.td-chip.is-on {
  background: var(--color-primary);
  border-color: var(--color-primary);
  color: var(--color-surface);
}

.td-chip--cal { gap: var(--sp-2); }
.td-chip--clear { padding: 0 var(--sp-3); color: var(--color-text-subtle); }

.td-dot { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; }

/* 켜진 칩이 그 값의 색을 입는다. '매우중요' 를 골랐는데 파란 칩이면
   카드·목록에 찍히는 빨간 우선순위 점과 서로 다른 말을 한다. */
.td-chip--pri-high.is-on {
  background: var(--color-danger-soft);
  border-color: var(--color-danger);
  color: var(--color-danger-strong);
}
.td-chip--pri-medium.is-on {
  background: var(--color-warning-soft);
  border-color: var(--color-warning);
  color: var(--color-warning-strong);
}
.td-chip--pri-low.is-on {
  background: var(--color-success-soft);
  border-color: var(--color-success);
  color: var(--color-success-strong);
}

/* 상태 칩도 같은 이유로 --task-* 토큰을 쓴다(StatusPill 과 같은 색). */
.td-chip--st-pending.is-on {
  background: var(--task-pending-bg);
  border-color: var(--color-border-strong);
  color: var(--task-pending-fg);
}
.td-chip--st-in_progress.is-on {
  background: var(--task-in-progress-bg);
  border-color: var(--task-in-progress);
  color: var(--task-in-progress-fg);
}
.td-chip--st-pending_approval.is-on {
  background: var(--task-pending-approval-bg);
  border-color: var(--task-pending-approval);
  color: var(--task-pending-approval-fg);
}
.td-chip--st-done.is-on {
  background: var(--task-done-bg);
  border-color: var(--task-done);
  color: var(--task-done-fg);
}

/* '나에게' 는 가장 잦은 선택이라 한 단계 크게 둔다. */
.td-chip--me {
  height: 44px;
  padding-left: 6px;
  font-weight: var(--fw-semibold);
  color: var(--color-text);
}
.td-chip--me::after { top: 0; bottom: 0; }
.td-chip--me :deep(.el-avatar) { font-size: var(--fs-sm); }

.td-picker, .td-select { width: 100%; margin-top: var(--sp-2); }
.td-picker :deep(.el-input__wrapper),
.td-select :deep(.el-input__wrapper),
.td-select :deep(.el-select__wrapper) {
  min-height: var(--tap-min);
}

/* ---------- 자세히 ---------- */
.td-more {
  display: flex;
  align-items: center;
  gap: 6px;
  align-self: flex-start;
  min-height: var(--tap-min);
  padding: 0;
  border: none;
  background: none;
  font-family: inherit;
  font-size: var(--fs-md);
  font-weight: var(--fw-semibold);
  color: var(--color-primary);
  cursor: pointer;
}
.td-more__caret { transition: transform var(--dur) var(--ease); }
.td-more__caret.is-open { transform: rotate(180deg); }

.td-more-body {
  display: flex;
  flex-direction: column;
  gap: var(--sp-4);
  padding-top: var(--sp-4);
  border-top: 1px solid var(--color-border);
}

.td-switch { display: flex; align-items: flex-start; gap: var(--sp-3); }
.td-switch__text { flex: 1; min-width: 0; }
.td-switch__text .field-label { margin-bottom: 0; }
.td-switch .td-hint { margin-top: 2px; }

/* ---------- 푸터 ---------- */
.td-fmain { height: var(--tap-lg); font-size: var(--fs-lg); font-weight: var(--fw-semibold); }
.td-fmore { height: var(--tap-lg); color: var(--color-text-muted); }

@media (prefers-reduced-motion: reduce) {
  .td-more__caret { transition: none; }
}
</style>
